import { useState } from 'react';

import { ScreenReaderOnly, Typography } from '@capsule/consumer-design-system';

import styled, { css } from 'styled-components';

import { ItemQuantityPicker } from 'src/app/components/Pages/Essentials/CheckoutAddons/ItemQuantityPicker';
import type { OTCItemV2 } from 'src/app/types';

import { EssentialsItemDetailsModal } from '../EssentialsItemDetailsModal/EssentialsItemDetailsModal';

const Container = styled.div`
  transition: transform ${({ theme }) => theme.animation.typical};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s2};
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  background-color: ${({ theme }) => theme.colors.transparent};
  color: ${({ theme }) => theme.colors.capsuleBlue50};
  user-select: none;

  ${({ theme }) => theme.mediaQueries.small} {
    padding-left: 15px;
    padding-right: 15px;
  }

  ${({ theme }) => theme.mediaQueries.smallDown} {
    gap: ${({ theme }) => theme.space.s1};
  }

  /** just for focus-visible outline to look nice */
  border-radius: ${({ theme }) => theme.radii.r100};
  &:focus-visible {
    outline-offset: 1px;
    outline: 1px solid ${({ theme }) => theme.colors.capsuleBlue50};
  }
`;

const TopRow = styled.div`
  display: flex;
  position: relative;
`;

const OTCImage = styled.img`
  transition: all ${({ theme }) => theme.animation.typical};
  object-fit: contain;
  width: 100%;
  height: 100px;
  padding: ${({ theme }) => theme.space.s4};
`;

export interface AddonOTCItemProps extends Omit<OTCItemV2, 'quantity'> {
  formattedPrice: string;
  onAddToCart?: () => void;
  onRemoveFromCart?: () => void;
}

const Badge = styled.div`
  padding: 2px 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.capsuleGreen10};
  height: 24px;
  width: 75px;
  position: absolute;
  top: 80px;
`;

export const AddonOTCItem = ({
  dosage,
  id,
  name,
  price,
  imageURLs,
  description,
  formattedPrice,
  pharmacistNotes,
  activeIngredients,
  quantityInStock,
  minQuantity,
  maxQuantity,
  badge,
  onAddToCart,
  onRemoveFromCart,
}: AddonOTCItemProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isHsaFsaEligible = badge?.name === 'HSA/FSA';

  return (
    <Container>
      <EssentialsItemDetailsModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        name={name}
        dosage={dosage}
        description={description}
        pharmacistNotes={pharmacistNotes}
        activeIngredients={activeIngredients}
        id={id}
        price={price}
        minQuantity={minQuantity}
        maxQuantity={maxQuantity}
        unavailableReasons={[]}
        quantityInStock={quantityInStock}
        imageURLs={imageURLs}
      />
      <TopRow>
        <OTCImage src={imageURLs[0]} alt="" onClick={() => setIsModalOpen(true)} />

        <ScreenReaderOnly>
          {name}, {dosage}, {formattedPrice}.
        </ScreenReaderOnly>

        <ItemQuantityPicker
          itemId={id}
          price={price}
          maxQuantity={maxQuantity}
          onAddToCart={onAddToCart}
          onRemoveFromCart={onRemoveFromCart}
        />
      </TopRow>

      <article
        aria-hidden
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          text-align: left;
          gap: ${({ theme }) => theme.space.s2};
        `}
        onClick={() => setIsModalOpen(true)}
      >
        {isHsaFsaEligible && (
          <Badge>
            <Typography variant="small">HSA/FSA</Typography>
          </Badge>
        )}
        <Typography
          variant="body"
          as="header"
          css={css`
            display: box; /* Enable block formatting for line clamping */
            /* stylelint-disable-next-line */
            display: -webkit-box;
            -webkit-line-clamp: 3; /* Number of lines to show */
            /* stylelint-disable-next-line property-no-unknown */
            box-orient: vertical; /* Set the box orientation to vertical */
            /* stylelint-disable-next-line property-no-vendor-prefix */
            -webkit-box-orient: vertical;
            overflow: hidden; /* Hide overflowing content */
            text-overflow: ellipsis; /* Add ellipsis for overflowing text */
          `}
        >
          {name}
        </Typography>

        {dosage && (
          <Typography
            variant="small"
            css={css`
              display: box; /* Enable block formatting for line clamping */
              /* stylelint-disable-next-line */
              display: -webkit-box;
              -webkit-line-clamp: 2; /* Number of lines to show */
              /* stylelint-disable-next-line property-no-unknown */
              box-orient: vertical; /* Set the box orientation to vertical */
              /* stylelint-disable-next-line property-no-vendor-prefix */
              -webkit-box-orient: vertical;
              overflow: hidden; /* Hide overflowing content */
              text-overflow: ellipsis; /* Add ellipsis for overflowing text */
            `}
          >
            {dosage}
          </Typography>
        )}

        <Typography variant="body">{formattedPrice}</Typography>
      </article>
    </Container>
  );
};
